/**
 * The GM window → broadcast screen link.
 *
 * Local-first means no server: the broadcast screen is a SECOND WINDOW of the
 * same browser (shared to Discord, or dragged onto the table's TV). The GM
 * window posts the campaign over a BroadcastChannel whenever it changes
 * (throttled), plus public dice rolls; the screen listens. On first paint the
 * screen reads the GM's autosave read-only (persistence/storage.ts
 * `readGmCampaign`), and when BroadcastChannel is missing it falls back to
 * the `storage` event of the GM's autosave key (≈1 s behind: the autosave
 * debounce).
 *
 * The screen never writes anything back.
 */
import { useEffect, useState } from 'react';
import type { Campaign } from '@/domain/campaign';
import { GM_NAMESPACE, readGmCampaign } from '@/persistence/storage';
import { getState, subscribe } from '@/state/store';

const CHANNEL = 'atla-cast';
const THROTTLE_MS = 120;

export interface PublicRoll {
  id: string;
  who: string;
  label: string;
  text: string;
  total: number;
  at: number;
}

type CastMessage =
  | { type: 'campaign'; campaign: Campaign }
  | { type: 'roll'; roll: PublicRoll }
  | { type: 'hello' };

function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
}

/**
 * GM side: publish the campaign on every change (throttled) and answer a
 * newly opened screen's "hello" at once. Returns the uninstaller.
 */
export function installCastPublisher(): () => void {
  const channel = openChannel();
  if (!channel) return () => undefined;
  let last: Campaign | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const send = (): void => {
    timer = null;
    const campaign = getState().campaign;
    if (!campaign || campaign === last) return;
    last = campaign;
    try {
      channel.postMessage({ type: 'campaign', campaign } satisfies CastMessage);
    } catch {
      // a campaign too large to clone: the screen falls back to the autosave
    }
  };

  const unsubscribe = subscribe(() => {
    if (getState().campaign === last || timer !== null) return;
    timer = setTimeout(send, THROTTLE_MS);
  });
  channel.onmessage = (event: MessageEvent<CastMessage>) => {
    if (event.data?.type === 'hello') {
      last = null;
      send();
    }
  };
  return () => {
    unsubscribe();
    if (timer !== null) clearTimeout(timer);
    channel.close();
  };
}

/** GM side: show a roll on the broadcast screen. */
export function publishRoll(roll: Omit<PublicRoll, 'id' | 'at'>): void {
  const channel = openChannel();
  if (!channel) return;
  try {
    channel.postMessage({
      type: 'roll',
      roll: { ...roll, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, at: Date.now() },
    } satisfies CastMessage);
  } finally {
    channel.close();
  }
}

/** Screen side: the GM's campaign, live, plus the last public rolls. */
export function useCastFeed(): { campaign: Campaign | null; rolls: PublicRoll[]; live: boolean } {
  const [campaign, setCampaign] = useState<Campaign | null>(() => (typeof window === 'undefined' ? null : readGmCampaign()));
  const [rolls, setRolls] = useState<PublicRoll[]>([]);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const channel = openChannel();
    if (channel) {
      channel.onmessage = (event: MessageEvent<CastMessage>) => {
        const data = event.data;
        if (data?.type === 'campaign') {
          setCampaign(data.campaign);
          setLive(true);
        } else if (data?.type === 'roll') {
          setRolls((current) => [data.roll, ...current].slice(0, 6));
        }
      };
      channel.postMessage({ type: 'hello' } satisfies CastMessage);
    }
    const onStorage = (event: StorageEvent): void => {
      if (event.key === `${GM_NAMESPACE}:autosave:v1`) {
        const fresh = readGmCampaign();
        if (fresh) setCampaign(fresh);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      channel?.close();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return { campaign, rolls, live };
}
